import * as React from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import { Button } from './button';
import { cn } from '../../lib/utils';

export interface ModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  title?: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'default' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: 'max-w-sm w-full mx-2 sm:mx-4 md:mx-auto',
  default: 'max-w-md w-full mx-2 sm:mx-4 md:mx-auto',
  lg: 'max-w-lg w-full mx-2 sm:mx-4 md:mx-auto',
  xl: 'max-w-xl w-full mx-2 sm:mx-4 md:mx-auto',
  full: 'max-w-full mx-2 sm:mx-4',
};

const Modal: React.FC<ModalProps> = ({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  size = 'default',
  showCloseButton = true,
  className,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent 
        className={cn(
          sizeClasses[size], 
          'max-h-[95vh] overflow-y-auto',
          className
        )}
        showCloseButton={false}
        onPointerDownOutside={(e) => {
          // Prevent closing when clicking on toast notifications
          const target = e.target as Element;
          if (target.closest('[data-sonner-toast]')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="pb-2">
          {title && (
            <DialogTitle className="text-lg sm:text-xl pr-8">
              {title}
            </DialogTitle>
          )}
          {description && (
            <DialogDescription className="text-sm sm:text-base">
              {description}
            </DialogDescription>
          )}
          {showCloseButton && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 h-8 w-8 sm:right-4 sm:top-4 sm:h-6 sm:w-6 touch-manipulation"
              onClick={() => onOpenChange?.(false)}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          )}
        </DialogHeader>
        <div className="py-2 sm:py-4">
          {children}
        </div>
        {footer && (
          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-2 pt-4 border-t">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { Modal };